import React, { useEffect, useRef, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { Camera, Check, KeyRound, Trash2 } from "lucide-react";

import { useAuthedApi } from "@/api/useAuthedApi";
import { AVATAR_CHANGED_EVENT, type UserProfile } from "@/api/profileApi";
import type { LearningSettings } from "@/api/learningSettingsApi";
import { getCroppedBlob } from "@/lib/cropImage";
import { refresh } from "@/auth/useAuth";
import { Button, TextInput, Spinner } from "@/components/ui";
import { cn } from "@/lib/cn";
import { ConversationCaptureSetting } from "./ConversationCaptureSetting";

const FIELD =
  "w-full rounded-[10px] bg-surface border border-edge px-3 py-2 text-sm text-ink " +
  "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25";

/** Grouped settings block: a title + optional description above fields, then a
 *  right-aligned save control. No card background — groups blend into the modal
 *  and are separated only by hairline dividers (see the container below). */
function Group({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <section className="py-6 first:pt-0 last:pb-0 space-y-4">
      <div className="space-y-1">
        <h3 className="text-[14px] font-semibold text-ink m-0">{title}</h3>
        {description && <p className="text-[12.5px] text-ink-muted m-0">{description}</p>}
      </div>
      {children}
      {footer && <div className="flex items-center justify-end gap-3 pt-1">{footer}</div>}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-ink">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-ink-muted m-0">{hint}</p>}
    </div>
  );
}

type SaveStatus = "idle" | "saving" | "saved";

/** Save button with an in-flight spinner and a transient "Saved" tick. */
function SaveButton({ label, onSave }: { label: string; onSave: () => Promise<void> }) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const run = async () => {
    setStatus("saving");
    try {
      await onSave();
      setStatus("saved");
      timer.current = setTimeout(() => setStatus("idle"), 2200);
    } catch {
      setStatus("idle");
    }
  };

  return (
    <>
      {status === "saved" && (
        <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-accent">
          <Check size={15} strokeWidth={2.6} /> Saved
        </span>
      )}
      {/* The group around it is the scope (name / preferred_name / learning). */}
      <Button size="sm" data-ph="save" onClick={run} disabled={status === "saving"}>
        {status === "saving" && <Spinner size={16} className="text-white" />}
        {label}
      </Button>
    </>
  );
}

/** Profile settings, embedded as a tab inside the Settings modal. */
export function ProfilePanel() {
  const { profile, learningSettings } = useAuthedApi();
  const [data, setData] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [preferred, setPreferred] = useState("");
  const [learning, setLearning] = useState<LearningSettings | null>(null);
  const [avatarVersion, setAvatarVersion] = useState(0);

  // Crop dialog state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropBusy, setCropBusy] = useState(false);
  const cropAreaRef = useRef<Area | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // A failed profile.get() previously left `data` null forever -> the panel
    // spun indefinitely (seen live). Catch: fall back to an empty profile so
    // the form still renders and the spinner clears.
    void profile
      .get()
      .then((p) => {
        setData(p);
        setFirstName(p.first_name);
        setLastName(p.last_name);
        setPreferred(p.preferred_name);
      })
      .catch(() => {
        setData({
          first_name: "",
          last_name: "",
          preferred_name: "",
          has_avatar: false,
        } as UserProfile);
      });
    void learningSettings
      .get()
      .then(setLearning)
      .catch(() => setLearning(null));
  }, [profile, learningSettings]);

  const saveIdentity = async () => {
    const res = await profile.update({ first_name: firstName, last_name: lastName });
    if (res.name_changed) await refresh();
  };

  const savePreferred = async () => {
    await profile.update({ preferred_name: preferred });
  };

  const saveLearning = async () => {
    if (!learning) return;
    await learningSettings.update({
      mode: learning.mode,
      dialogue_threshold: learning.dialogue_threshold,
    });
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setCropSrc(URL.createObjectURL(f));
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
    e.target.value = "";
  };

  const confirmCrop = async () => {
    if (!cropSrc || !cropAreaRef.current) return;
    setCropBusy(true);
    try {
      const blob = await getCroppedBlob(cropSrc, cropAreaRef.current);
      await profile.uploadAvatar(blob);
      setCropSrc(null);
      setAvatarVersion((v) => v + 1);
      setData((d) => (d ? { ...d, has_avatar: true } : d));
      window.dispatchEvent(new CustomEvent(AVATAR_CHANGED_EVENT));
    } finally {
      setCropBusy(false);
    }
  };

  const removeAvatar = async () => {
    await profile.deleteAvatar();
    setData((d) => (d ? { ...d, has_avatar: false } : d));
    window.dispatchEvent(new CustomEvent(AVATAR_CHANGED_EVENT));
  };

  if (!data) {
    return (
      <div className="grid place-items-center py-24 text-ink-muted">
        <Spinner />
      </div>
    );
  }

  const initials = (data.email || "U").slice(0, 2).toUpperCase();

  return (
    <div data-ph-scope="profile" className="px-7 py-6">
      <div className="divide-y divide-hairline">
      <Group
        title="Profile"
        description="Your name is shown across the workspace and in shared activity."
        // display: contents keeps the scope wrapper out of the footer's flex layout.
        footer={
          <span data-ph-scope="name" style={{ display: "contents" }}>
            <SaveButton label="Save profile" onSave={saveIdentity} />
          </span>
        }
      >
        <div data-ph-scope="photo" className="flex items-center gap-5">
          {data.has_avatar ? (
            <img
              src={profile.avatarHref(avatarVersion)}
              alt=""
              className="h-20 w-20 rounded-full object-cover border border-hairline"
            />
          ) : (
            <span className="h-20 w-20 rounded-full bg-accent text-white grid place-items-center text-2xl font-bold select-none">
              {initials}
            </span>
          )}
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" data-ph="upload" onClick={() => fileRef.current?.click()}>
                <Camera size={15} /> {data.has_avatar ? "Change photo" : "Upload photo"}
              </Button>
              {data.has_avatar && (
                <Button variant="ghost" size="sm" data-ph="delete" onClick={removeAvatar}>
                  <Trash2 size={15} /> Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-ink-muted m-0">JPG, PNG, or WebP. Up to 5MB.</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onPickFile}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" htmlFor="first-name">
            <TextInput id="first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last name" htmlFor="last-name">
            <TextInput id="last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
        </div>

        <Field label="Email" hint="Your sign-in address. Managed by your identity provider.">
          <div className="text-sm text-ink-secondary py-1">{data.email}</div>
        </Field>
      </Group>

      <Group title="How do you want Artie to address you">
        <div data-ph-scope="preferred_name" className="flex items-end gap-3">
          <div className="flex-1">
            <Field label="Preferred name" htmlFor="preferred-name">
              <TextInput
                id="preferred-name"
                value={preferred}
                placeholder="e.g. Ada"
                onChange={(e) => setPreferred(e.target.value)}
              />
            </Field>
          </div>
          <SaveButton label="Save name" onSave={savePreferred} />
        </div>
      </Group>

      {learning && (
        <Group
          title="Learning"
          description="Control how Artie turns your conversations into reusable skills."
          footer={
          <span data-ph-scope="learning" style={{ display: "contents" }}>
            <SaveButton label="Save learning" onSave={saveLearning} />
          </span>
        }
        >
          <div data-ph-scope="learning" className="grid gap-4 sm:grid-cols-2">
            <Field label="Mode" htmlFor="learning-mode" hint="Automatic creates skills for you; manual asks first.">
              <select
                id="learning-mode"
                data-ph="select_mode"
                value={learning.mode}
                onChange={(e) => setLearning({ ...learning, mode: e.target.value as "auto" | "manual" })}
                className={cn(FIELD, "cursor-pointer")}
              >
                <option value="manual">Manual</option>
                <option value="auto">Automatic</option>
              </select>
            </Field>
            <Field
              label="Dialogue threshold"
              htmlFor="dialogue-threshold"
              hint="Conversations before Artie proposes a skill."
            >
              <TextInput
                id="dialogue-threshold"
                type="number"
                min={1}
                max={1000}
                value={learning.dialogue_threshold}
                onChange={(e) => setLearning({ ...learning, dialogue_threshold: Number(e.target.value) })}
              />
            </Field>
          </div>
        </Group>
      )}

      <ConversationCaptureSetting />

      <Group
        title="Security"
        description="Passwords are handled by your identity provider in a secure console."
      >
        {/* Both carry the tag: the scanner counts the link and the button inside it. */}
        <a href={data.password_change_url} data-ph="open_password" target="_blank" rel="noreferrer" className="inline-block">
          <Button variant="secondary" size="sm" data-ph="open_password">
            <KeyRound size={15} /> Change password
          </Button>
        </a>
      </Group>
      </div>

      {/* Crop dialog */}
      {cropSrc && (
        <div
          data-ph-scope="photo_crop"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/45"
          onPointerDown={(e) => {
            if (e.target === e.currentTarget && !cropBusy) setCropSrc(null);
          }}
        >
          <div className="w-full max-w-md rounded-lg bg-canvas border border-edge shadow-2xl overflow-hidden">
            <div className="px-6 pt-5 pb-3">
              <h2 className="text-lg font-bold text-ink m-0">Crop your photo</h2>
              <p className="text-[13px] text-ink-muted m-0 mt-0.5">Drag to reposition, scroll to zoom.</p>
            </div>
            <div className="relative mx-6 h-64 rounded-md overflow-hidden bg-black/80">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_a, px) => {
                  cropAreaRef.current = px;
                }}
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4">
              <Button variant="secondary" data-ph="cancel" onClick={() => setCropSrc(null)} disabled={cropBusy}>
                Cancel
              </Button>
              <Button data-ph="save" onClick={confirmCrop} disabled={cropBusy}>
                {cropBusy && <Spinner size={16} className="text-white" />}
                Save photo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
